import { toast } from "@/components/ui/use-toast";
import { gql } from "@/gql/__generated__";
import { useMutation } from "@apollo/client";
import { ActiveOrganization } from "../user/useUserActiveOrganization";
import { GET_HAS_CUSTOMER_ACCOUNT } from "./hasCustomerAccount";

export const CREATE_CUSTOMER_ACCOUNT_PORTAL_URL = gql(`
  mutation CREATE_CUSTOMER_ACCOUNT_PORTAL_URL($organizationId: ID!) {
    customerAccountCreatePortalURL(organizationId: $organizationId) {
      url
    }
  }
`);

interface Props {
  organization: ActiveOrganization;
}

export function useCustomerAccountPortal({ organization }: Props) {
  const [createPortalLink, { loading }] = useMutation(
    CREATE_CUSTOMER_ACCOUNT_PORTAL_URL,
    {
      variables: { organizationId: organization.id },
      update: (cache, result) => {
        if (!result.data?.customerAccountCreatePortalURL.url) return cache;
        cache.updateQuery(
          {
            query: GET_HAS_CUSTOMER_ACCOUNT,
            variables: { organizationSlug: organization.slug }
          },
          (data) => {
            if (data?.organization.__typename !== "Organization") return data;
            return {
              ...data,
              organization: {
                ...data?.organization,
                hasCustomerAccount: true
              }
            };
          }
        );

        return cache;
      }
    }
  );

  const openCustomerAccountPortal = async () => {
    try {
      const { data, errors } = await createPortalLink();
      if (errors) {
        errorToast();
        console.error(errors);
        return;
      }
      const url = data?.customerAccountCreatePortalURL.url;
      if (url) {
        window.open(url);
      } else {
        console.error("No url returned on customerAccountCreatePortalURL");
        errorToast();
      }
    } catch (err) {
      errorToast();
      console.error(err);
    }
  };

  return { openCustomerAccountPortal, createPortalLink, loading };
}

function errorToast() {
  toast({
    title: "Internal error",
    description:
      "An error occured when trying to get the link for your customer portal. Please try again or report the issue.",
    variant: "destructive"
  });
}
